import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getStockItem } from '@/api/stock';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDateTime } from '@/lib/dateFormat';
import type { StockItem, StockTransaction } from '@/types/oscar';

/**
 * What actually happened to a stock line (095).
 *
 * Anthony, 08/10/2026: *"we also need to have an audit line in the stock screen
 * of allocations from it or adjustments so we can see what happened."*
 *
 * ⛔ THE RUNNING BALANCE IS THE POINT. Every movement has been recorded since
 * migration 004 — none of it was ever shown, so the screen displayed the ANSWER
 * with no way to see the working. A count on its own cannot be checked, because
 * a wrong count and a right count look identical. On 07/10/2026 an allocation
 * of one iPhone took the line from 16 to 17 — up, by giving one away — and it
 * went unnoticed until somebody happened to look at the number. A balance
 * beside each movement makes that visible at a glance.
 *
 * ⚠ READ ONLY. Nothing here changes stock; recording a movement is the
 * "Movement…" dialog, and mixing the two would put a destructive control inside
 * the screen people open to understand a mistake.
 */

/** ⚠ Plain words. "CheckOut" is a database value, not something to show a person. */
const WHAT_HAPPENED: Record<string, string> = {
  Receipt: 'Came in',
  CheckOut: 'Went out',
  CheckIn: 'Came back',
  Consume: 'Used up',
  Adjust: 'Counted',
};

function MovementRow({ m }: { m: StockTransaction }) {
  // ⚠ An Adjust is a SIGHTED COUNT, not a delta: the stored quantity is the
  // movement needed to reach the number somebody counted. Showing "-2" there
  // would read as two units leaving the building, which is not what happened.
  const qty = m.IsSightedCount
    ? `counted ${m.RunningBalance}`
    : `${m.Qty > 0 ? '+' : ''}${m.Qty}`;

  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-1 pr-3 align-top whitespace-nowrap text-darkGrey/70">
        {formatDateTime(m.TxnDate)}
      </td>
      <td className="py-1 pr-3 align-top font-semibold text-darkGrey">
        {WHAT_HAPPENED[m.TxnType] ?? m.TxnType}
      </td>
      <td className={`py-1 pr-3 align-top text-right tabular-nums ${
        !m.IsSightedCount && m.Qty < 0 ? 'text-orange' : 'text-darkGrey'
      }`}>
        {qty}
      </td>
      {/* ⛔ The balance AFTER this movement — what the line read at that moment. */}
      <td className="py-1 pr-3 align-top text-right font-semibold tabular-nums text-navy">
        {m.RunningBalance}
      </td>
      <td className="py-1 pr-3 align-top text-darkGrey/80">
        {/* ⚠ The REQUEST, from its own key — never parsed out of the note,
            which is a sentence written for a person. */}
        {m.SubmissionId !== null ? (
          <Link
            to={`/hardware-requests/${m.SubmissionId}`}
            className="font-semibold text-navy hover:underline"
          >
            Request #{m.SubmissionId}
          </Link>
        ) : (
          <span className="text-darkGrey/50">—</span>
        )}
        {m.FulfilmentStatus && (
          <span className="ml-1 text-darkGrey/60">({m.FulfilmentStatus})</span>
        )}
        {m.PersonName && <div className="text-darkGrey/70">to {m.PersonName}</div>}
      </td>
      <td className="py-1 align-top text-darkGrey/70">
        <div>{m.PerformedBy}</div>
        {m.Notes && <div className="text-darkGrey/60">{m.Notes}</div>}
      </td>
    </tr>
  );
}

export function HistoryDialog({ item, onClose }: { item: StockItem; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['stock-item', item.StockItemId],
    queryFn: ({ signal }) => getStockItem(item.StockItemId, signal),
  });

  const movements = query.data?.Transactions ?? [];

  // ⛔ THE CHECK WORTH SURFACING. The newest movement's balance IS what the
  // line should read — QtyOnHand is the running sum of the ledger. When they
  // disagree, a count was written without a movement behind it, and that gap is
  // the thing somebody needs to know about rather than something to hide.
  const newest = movements[0];
  const disagrees = newest !== undefined && newest.RunningBalance !== item.QtyOnHand;

  return (
    // ⚠ hideCancel: READ ONLY, so there is nothing to cancel. Without it the
    // dialog shows Cancel AND Close - two buttons doing one job, one of them
    // implying a change is being abandoned.
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`History — ${item.Name}`}
      confirmLabel="Close"
      hideCancel
      onConfirm={onClose}
    >
      <div className="space-y-2">
        <p className="text-xs text-darkGrey/70">
          Every movement on this line, newest first. The balance is what the line read
          straight after each one.
        </p>

        {query.isPending && <p className="text-xs text-darkGrey/70">Loading…</p>}

        {query.isError && (
          <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">
            Could not load the history for this line.
          </div>
        )}

        {!query.isPending && !query.isError && movements.length === 0 && (
          <p className="text-xs text-darkGrey/70">
            Nothing has been recorded against this line yet.
          </p>
        )}

        {disagrees && (
          <div role="alert" className="rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
            <strong>This line reads {item.QtyOnHand}, but its movements add up to {newest.RunningBalance}.</strong>{' '}
            Something set the count without recording a movement. A sighted count on this line
            — Movement, then Counted — writes the movement that is missing.
          </div>
        )}

        {movements.length > 0 && (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white text-left text-darkGrey/70">
                <tr className="border-b border-border">
                  <th className="py-1 pr-3 font-semibold">When</th>
                  <th className="py-1 pr-3 font-semibold">What</th>
                  <th className="py-1 pr-3 text-right font-semibold">Change</th>
                  <th className="py-1 pr-3 text-right font-semibold">Balance</th>
                  <th className="py-1 pr-3 font-semibold">For</th>
                  <th className="py-1 font-semibold">By</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => (
                  <MovementRow key={m.StockTransactionId} m={m} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AlertDialog>
  );
}
